import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, MapPin, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Cloud, TrendingUp, BarChart3 } from 'lucide-react';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import TriggeredAlertCard from './components/TriggeredAlertCard';
import { MOCK_WEEK_PRESETS } from './mockTriggeredAlerts';
import {
  useGetDefaultAlertsNotifications,
  useGetCustomAlertsNotifications,
  getCurrentWeekNumber,
  getInitialSelectedWeeks,
} from '../../utils/api-hooks/alerts';
import PageSkeleton from '../../components/PageSkeleton';

const weeksForPreset = (preset) => {
  const current = getCurrentWeekNumber();
  if (preset === '1w') return [current].filter((week) => week >= 1);
  if (preset === '2w') return [current - 1, current].filter((week) => week >= 1);
  return getInitialSelectedWeeks();
};

const originOf = (alert) =>
  alert.origin || alert.region || alert.country || '';

const ALERT_TYPE_CONFIG = {
  volume: {
    key: 'volume',
    icon: BarChart3,
    color: 'text-amber-600',
    well: 'bg-[#FFF6E5]',
  },
  weather: {
    key: 'weather',
    icon: Cloud,
    color: 'text-blue-500',
    well: 'bg-[#EAF3FF]',
  },
  market: {
    key: 'market',
    icon: TrendingUp,
    color: 'text-purple-500',
    well: 'bg-[#F3E8FF]',
  },
  price: {
    key: 'price',
    icon: DollarSign,
    color: 'text-bloom-green',
    well: 'bg-[#E9FBF0]',
  },
};

const TYPE_ORDER = ['volume', 'weather', 'market', 'price'];

const normalizeAlertType = (alertType, specificAlertType) => {
  const type = `${alertType || ''} ${specificAlertType || ''}`.toLowerCase();
  if (type.includes('rain') || type.includes('temperature') || type.includes('weather')) {
    return 'weather';
  }
  if (type.includes('volume')) return 'volume';
  if (type.includes('price') || type.includes('fob')) return 'price';
  if (type.includes('market') || type.includes('news')) return 'market';
  const raw = (alertType || '').toLowerCase();
  if (raw === 'rain' || raw === 'temperature') return 'weather';
  return raw || 'market';
};

const normalizeImpact = (impact) => {
  const level = (impact || '').toLowerCase();
  if (level === 'alto') return 'high';
  if (level === 'medio') return 'medium';
  if (level === 'bajo') return 'low';
  return level;
};

const FilterSelect = ({ icon: Icon, value, options, onChange, labelKey = 'label' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const display =
    typeof options[0] === 'string'
      ? value
      : options.find((o) => o.id === value)?.[labelKey] || value;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-[9px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] px-3 py-2 text-[13px] leading-none text-[#282A29] dark:text-white hover:bg-[#F9F9F9] dark:hover:bg-[#282a29] transition-colors"
      >
        <Icon size={16} className="text-bloom-green shrink-0" />
        <span className="font-['Inter'] whitespace-nowrap">{display}</span>
        <ChevronDown
          size={14}
          className={`text-[#828382] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 min-w-full overflow-hidden rounded-[9px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] shadow-lg">
          {options.map((opt) => {
            const id = typeof opt === 'string' ? opt : opt.id;
            const text = typeof opt === 'string' ? opt : opt[labelKey];
            const selected = id === value;
            return (
              <button
                key={id}
                type="button"
                onClick={() => {
                  onChange(id);
                  setOpen(false);
                }}
                className={`block w-full whitespace-nowrap px-3 py-2 text-left font-['Inter'] text-[13px] transition-colors ${
                  selected
                    ? 'bg-[#E3F5EB] text-[#12823F]'
                    : 'text-[#282A29] dark:text-white hover:bg-[#F0F5F2] dark:hover:bg-[#282a29]'
                }`}
              >
                {text}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PriorityPill = ({ active, onClick, children }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-[9px] border px-3 py-1.5 font-['Inter'] text-[13px] leading-none transition-colors ${
        active
          ? 'border-bloom-green text-bloom-green bg-white dark:bg-[#1d1d1d]'
          : 'border-[#D5D8D6] dark:border-[#5d625f] text-[#5D625F] dark:text-[#C3C4C3] bg-white dark:bg-[#1d1d1d] hover:border-[#C3C4C3]'
      }`}
    >
      {children}
    </button>
  );
};

/**
 * Design-aligned Triggered Alerts section used by the Alerts Hub.
 * Reads live default + custom notification endpoints.
 */
const TriggeredAlertsSection = () => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const [alertTab, setAlertTab] = useState('all'); // all | smart | custom
  const [origin, setOrigin] = useState('All origins');
  const [weekPreset, setWeekPreset] = useState('4w');
  const [priority, setPriority] = useState('all'); // all | high | medium | low
  const [collapsed, setCollapsed] = useState({});
  const [dismissed, setDismissed] = useState(() => new Set());

  const selectedWeeks = useMemo(() => weeksForPreset(weekPreset), [weekPreset]);
  const {
    data: defaultNotificationsData,
    isLoading: isLoadingDefault,
    isError: isErrorDefault,
    error: errorDefault,
    refetch: refetchDefault,
  } = useGetDefaultAlertsNotifications(selectedWeeks);
  const {
    data: customNotificationsData,
    isLoading: isLoadingCustom,
    isError: isErrorCustom,
    error: errorCustom,
    refetch: refetchCustom,
  } = useGetCustomAlertsNotifications(selectedWeeks);

  const isLoading = isLoadingDefault || isLoadingCustom;
  const isError = isErrorDefault && isErrorCustom;
  const error = errorDefault || errorCustom;
  const partialError = (isErrorDefault || isErrorCustom) && !isError;

  const liveAlerts = useMemo(() => {
    const smart = (defaultNotificationsData?.alerts || []).map((alert) => ({
      ...alert,
      tab_type: alert.tab_type || 'default',
    }));
    const custom = (customNotificationsData?.alerts || []).map((alert) => ({
      ...alert,
      tab_type: alert.tab_type || 'custom',
    }));
    return [...smart, ...custom].filter(
      (alert, index, arr) =>
        arr.findIndex((other) => other.unique_key === alert.unique_key) === index
    );
  }, [defaultNotificationsData, customNotificationsData]);

  const originOptions = useMemo(() => {
    const names = new Set();
    liveAlerts.forEach((alert) => {
      const name = originOf(alert);
      if (name) names.add(name);
    });
    return ['All origins', ...Array.from(names).sort((a, b) => a.localeCompare(b))];
  }, [liveAlerts]);

  const alertTabs = [
    { id: 'all', label: t('alerts.triggered.all') },
    { id: 'smart', label: t('alerts.triggered.smart') },
    { id: 'custom', label: t('alerts.triggered.custom') },
  ];

  const filteredAlerts = useMemo(() => {
    return liveAlerts.filter((alert) => {
      if (dismissed.has(alert.unique_key)) return false;

      if (alertTab === 'smart' && alert.tab_type !== 'default') return false;
      if (alertTab === 'custom' && alert.tab_type !== 'custom') return false;

      if (origin !== 'All origins' && originOf(alert) !== origin) return false;

      const impact = normalizeImpact(alert.market_impact);
      if (priority !== 'all' && impact !== priority) return false;

      return true;
    });
  }, [liveAlerts, alertTab, origin, priority, dismissed]);

  const priorityCounts = useMemo(() => {
    const base = liveAlerts.filter((a) => !dismissed.has(a.unique_key));
    const byTab = base.filter((alert) => {
      if (alertTab === 'smart') return alert.tab_type === 'default';
      if (alertTab === 'custom') return alert.tab_type === 'custom';
      return true;
    });
    return {
      all: byTab.length,
      high: byTab.filter((a) => normalizeImpact(a.market_impact) === 'high').length,
      medium: byTab.filter((a) => normalizeImpact(a.market_impact) === 'medium').length,
      low: byTab.filter((a) => normalizeImpact(a.market_impact) === 'low').length,
    };
  }, [liveAlerts, alertTab, dismissed]);

  const groupedAlerts = useMemo(() => {
    const groups = { volume: [], weather: [], market: [], price: [] };
    filteredAlerts.forEach((alert) => {
      const type = normalizeAlertType(alert.alert_type, alert.specific_alert_type);
      if (!groups[type]) groups[type] = [];
      groups[type].push({ ...alert, alert_type: type });
    });
    return groups;
  }, [filteredAlerts]);

  const visibleGroups = TYPE_ORDER.map((type) => [type, groupedAlerts[type] || []]).filter(
    ([, list]) => list.length > 0
  );
  const hasTriggered = visibleGroups.length > 0;

  const handleDismiss = (alert) => {
    setDismissed((prev) => new Set([...prev, alert.unique_key]));
  };

  return (
    <div className="flex flex-col w-full gap-4">
      {/* Header row */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
        <div className="min-w-0 flex-1">
          <h2 className="font-['Inter'] font-bold uppercase tracking-[0.06em] text-[#282A29] dark:text-white text-sm">
            {t('alerts.hub.triggeredSectionTitle')}
          </h2>
          <p className="mt-3 font-['Inter'] text-[14px] leading-snug text-[#5D625F] dark:text-[#C3C4C3]">
            {t('alerts.hub.triggeredSectionSubtitle')}
          </p>
        </div>

        <div className="inline-flex items-center gap-0 rounded-[10px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] p-1 self-start lg:self-start shrink-0">
          {alertTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setAlertTab(tab.id)}
              className={`px-4 py-1.5 rounded-[8px] font-['Inter'] font-medium transition-colors ${
                alertTab === tab.id
                  ? 'bg-bloom-green text-white'
                  : 'text-[#5D625F] dark:text-[#C3C4C3] hover:text-[#282A29] dark:hover:text-white'
              }`}
              style={{ fontSize: fontSize - 1 }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Priority pills (left) + Origin/Week selects (right) */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <PriorityPill active={priority === 'all'} onClick={() => setPriority('all')}>
            <span>All</span>
            <span className="font-semibold">{priorityCounts.all}</span>
          </PriorityPill>
          <PriorityPill active={priority === 'high'} onClick={() => setPriority('high')}>
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            <span>High</span>
            <span className="font-semibold">{priorityCounts.high}</span>
          </PriorityPill>
          <PriorityPill active={priority === 'medium'} onClick={() => setPriority('medium')}>
            <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
            <span>Medium</span>
            <span className="font-semibold">{priorityCounts.medium}</span>
          </PriorityPill>
          <PriorityPill active={priority === 'low'} onClick={() => setPriority('low')}>
            <span className="h-1.5 w-1.5 rounded-full bg-bloom-green" />
            <span>Low</span>
            <span className="font-semibold">{priorityCounts.low}</span>
          </PriorityPill>
        </div>

        <div className="flex flex-wrap items-center gap-[17px]">
          <FilterSelect
            icon={MapPin}
            value={origin}
            options={originOptions}
            onChange={setOrigin}
          />
          <FilterSelect
            icon={Calendar}
            value={weekPreset}
            options={MOCK_WEEK_PRESETS}
            onChange={setWeekPreset}
            labelKey="label"
          />
        </div>
      </div>

      {partialError ? (
        <p className="font-['Inter'] text-[13px] text-red-500">
          {isErrorCustom
            ? t('alerts.hub.customLoadError', 'Custom alerts failed to load.')
            : t('alerts.hub.smartLoadError', 'Smart alerts failed to load.')}
        </p>
      ) : null}
      {isLoading ? (
        <PageSkeleton shape="alerts" />
      ) : isError ? (
        <div className="w-full flex flex-col items-center gap-3 py-12">
          <p className="text-red-500 font-['Inter']" style={{ fontSize }}>
            {error?.message || t('alerts.triggered.noAlertsInPeriod')}
          </p>
          <button
            type="button"
            onClick={() => {
              refetchDefault();
              refetchCustom();
            }}
            className="px-6 py-2 bg-bloom-green text-white rounded-lg hover:bg-bloom-green-hover transition-colors"
          >
            Retry
          </button>
        </div>
      ) : !hasTriggered ? (
        <div className="w-full flex justify-center py-12">
          <p className="text-[#828382] font-['Inter']" style={{ fontSize }}>
            {t('alerts.triggered.noAlertsInPeriod')}
          </p>
        </div>
      ) : (
        <div
          className={`grid items-start gap-6 ${
            visibleGroups.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'
          }`}
        >
          {visibleGroups.map(([type, alerts]) => {
            const config = ALERT_TYPE_CONFIG[type] || ALERT_TYPE_CONFIG.market;
            const Icon = config.icon;
            const isCollapsed = collapsed[type];
            const label = t(`alerts.${config.key}`, { defaultValue: config.key });
            return (
              <div key={type} className="flex min-w-0 flex-col gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((prev) => ({ ...prev, [type]: !prev[type] }))
                  }
                  className="flex items-center gap-2 pb-2 border-b border-bloom-green w-full"
                >
                  <span
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-[8px] ${config.well}`}
                  >
                    <Icon size={16} className={config.color} />
                  </span>
                  <span className="font-['Inter'] font-semibold text-[16px] leading-none text-[#282A29] dark:text-white">
                    {label}
                  </span>
                  <span className="font-['Inter'] font-semibold text-[16px] leading-none text-[#282A29] dark:text-white">
                    ({alerts.length})
                  </span>
                  <ChevronDown
                    size={18}
                    className={`ml-auto text-[#828382] transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                  />
                </button>
                {!isCollapsed && (
                  <div className="flex flex-col gap-3">
                    {alerts.map((alert) => (
                      <TriggeredAlertCard
                        key={alert.unique_key}
                        alert={alert}
                        onDismiss={handleDismiss}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TriggeredAlertsSection;
